import { Download, Star, Images, Inbox, type LucideProps } from 'lucide-react'
import type { ComponentType } from 'react'

import type { CreateShareLinkBody } from '../../api/types.gen'

/*
 |-----------------------------------------------------------------------------
 | Los flujos comerciales
 |-----------------------------------------------------------------------------
 | El modelo de datos guarda permisos, `max_selections` y `download_scope` por
 | separado (PLAN.md §7). Esta pantalla NO los expone así, y es la decisión de
 | diseño más importante del módulo.
 |
 | Con cuatro casillas de permisos y tres campos numéricos, un fotógrafo puede
 | construir combinaciones que no significan nada: marcar «elegir» sin cupo,
 | poner ámbito `selected` sin permiso de selección, dar descarga de todo en un
 | proofing. Ninguna falla en el backend; simplemente entregan algo distinto de
 | lo que el estudio vendió, y lo descubre cuando el cliente ya se ha bajado 400
 | fotos que había que cobrar aparte.
 |
 | El sector entrega de tres maneras. Se pregunta cuál, y el resto se deriva.
 |
 | `max_downloads` NO aparece en ningún sitio de esta interfaz, a propósito. No
 | es el cupo comercial —cuenta autorizaciones de URL, así que reintentar con
 | mala conexión lo consume, y en Perú eso es el caso normal— sino control de
 | abuso. Ofrecerlo junto a «cuántas fotos puede elegir» es invitar justo a la
 | confusión contra la que avisa el plan. El cupo comercial es `max_selections`.
 */

export type FlowId = 'entrega' | 'proofing' | 'proofing_descarga' | 'recepcion'

export interface Flow {
  id: FlowId
  label: string
  /** Qué recibe el cliente, dicho desde su lado. */
  summary: string
  /** Cuándo se usa. Ayuda a elegir sin tener que probar. */
  when: string
  icon: ComponentType<LucideProps>
  /*
   | La unión que acepta la API, no `string[]`.
   |
   | Con texto libre, una errata en un preset —`'downloads'` por `'download'`—
   | pasaba desapercibida hasta que un estudio creaba el enlace y el servidor lo
   | rechazaba con un 422 sin decir cuál de los cuatro estaba mal.
   */
  permissions: CreateShareLinkBody['permissions']
  downloadScope: 'all' | 'selected'
  /** Si pide un número de fotos contratadas. */
  needsQuota: boolean
  /**
   * Si el enlace RECIBE archivos en vez de entregarlos.
   *
   * Cambia dos cosas: el cupo que se pide son megas y no fotos, y el cliente
   * no ve la galería —el enlace no lleva `view`—.
   */
  receives?: boolean
}

export const FLOWS: readonly Flow[] = [
  {
    id: 'entrega',
    label: 'Entrega completa',
    summary: 'Ve y descarga todo, las veces que quiera.',
    when: 'La entrega final, cuando ya está todo pagado y retocado.',
    icon: Download,
    permissions: ['view', 'download'],
    downloadScope: 'all',
    needsQuota: false,
  },
  {
    id: 'proofing',
    label: 'Selección, sin descarga',
    summary: 'Elige sus favoritas. No puede descargar nada.',
    when: 'Le enseñas la sesión para que marque cuáles quiere retocar.',
    icon: Star,
    permissions: ['view', 'select'],
    downloadScope: 'all',
    needsQuota: true,
  },
  {
    id: 'proofing_descarga',
    label: 'Selección con descarga',
    summary: 'Elige sus favoritas y se lleva sólo ésas.',
    when: 'Vendiste un número de fotos y el cliente escoge cuáles.',
    icon: Images,
    permissions: ['view', 'select', 'download'],
    downloadScope: 'selected',
    needsQuota: true,
  },
  {
    id: 'recepcion',
    label: 'Recibir archivos',
    summary: 'Te manda archivos. No ve nada de lo tuyo.',
    when: 'Le pides el material de partida: fotos suyas, un logo, el briefing.',
    icon: Inbox,
    /*
     | `upload` a secas, sin `view`, y por eso hubo que hacer real ese permiso
     | en el backend: hasta ahora no lo comprobaba nadie, así que un enlace que
     | dijera «no ve nada de lo tuyo» habría enseñado la galería entera.
     |
     | La galería es de ida: lo que entra por aquí lo ve el estudio en su panel
     | y no se le enseña a nadie por el portal, ni siquiera por el enlace que lo
     | recibió. En una dirección que comparte una pareja, enseñarlo sería
     | ponerle delante a uno lo que envió el otro.
     */
    permissions: ['upload'],
    downloadScope: 'all',
    needsQuota: false,
    receives: true,
  },
]

export const flowById = (id: FlowId): Flow => FLOWS.find((f) => f.id === id) ?? FLOWS[0]!

/**
 * El camino de vuelta: de lo que hay guardado al flujo que lo describe.
 *
 * Hace falta porque la lista muestra enlaces creados antes de que existiera
 * esta pantalla —y por la API, que sigue aceptando cualquier combinación—. Se
 * reconoce por permisos y ámbito; lo que no encaje en ninguno se dice tal cual
 * en vez de mentir con la etiqueta más parecida.
 */
export function describeFlow(
  permissions: readonly string[],
  downloadScope: string,
): Flow | null {
  const has = (p: string): boolean => permissions.includes(p)

  // Primero el de recepción: es el único que no entrega nada, así que no puede
  // confundirse con los demás por mucho que comparta permisos.
  if (has('upload') && !has('view')) return flowById('recepcion')

  if (has('select') && has('download')) {
    return downloadScope === 'selected' ? flowById('proofing_descarga') : null
  }

  if (has('select')) return flowById('proofing')
  if (has('download')) return downloadScope === 'all' ? flowById('entrega') : null

  return null
}
